import React, { useState, useEffect } from 'react';
import { X, Building2, User, Mail, Phone, MapPin, FileText, CheckCircle, XCircle, Loader2, AlertCircle, Copy, Key } from 'lucide-react';
import { RetailerRequest } from '../../types';
import { approveRetailerRequest, rejectRetailerRequest } from '../../services/retailerService';
import { sendApprovalEmail } from '../../services/emailService';
import { createRetailerAccount } from '../../services/accountService';
import { auth } from '../../firebase';

interface RequestDetailModalProps {
  request: RetailerRequest;
  onClose: () => void;
  onUpdate: () => void;
}

const RequestDetailModal: React.FC<RequestDetailModalProps> = ({ request, onClose, onUpdate }) => {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createdCredentials, setCreatedCredentials] = useState<{ email: string; password: string } | null>(null);
  const [copiedField, setCopiedField] = useState<'email' | 'password' | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isApproving && !isRejecting) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, isApproving, isRejecting]);

  const handleApprove = async () => {
    setError('');
    setIsApproving(true);

    try {
      const adminEmail = auth.currentUser?.email || 'admin';

      // Create Firebase Auth account via Cloud Function
      const accountResult = await createRetailerAccount(
        request.email,
        request.contactName,
        request.id
      );

      // Approve in Firestore (creates retailer record)
      await approveRetailerRequest(request.id, adminEmail);

      // Try to send approval email with credentials
      try {
        await sendApprovalEmail({
          to_email: request.email,
          to_name: request.contactName,
          business_name: request.businessName,
          temp_password: accountResult.tempPassword
        });
        setSuccess('Account created and credentials sent to ' + request.email);
      } catch (emailErr) {
        // Email failed but account was created - show credentials to admin
        console.error('Email send failed:', emailErr);
        setSuccess('Account created. Email failed — please send credentials manually.');
      }

      // Store credentials so admin can copy/send manually if needed
      setCreatedCredentials({
        email: request.email,
        password: accountResult.tempPassword
      });

      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to approve request');
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    setError('');
    setIsRejecting(true);

    try {
      const adminEmail = auth.currentUser?.email || 'admin';
      await rejectRetailerRequest(request.id, adminEmail, rejectionReason);

      setSuccess('Request rejected.');

      setTimeout(() => {
        onUpdate();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to reject request');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleCopy = (text: string, field: 'email' | 'password') => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const isPending = request.status === 'pending';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm" onClick={onClose}></div>

      <div className="relative bg-paper-bright shadow-float w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-forge text-paper px-6 py-5 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-2">— Application</p>
          <h2 className="font-editorial italic font-light text-2xl text-paper-warm tracking-tight">
            {request.businessName}.
          </h2>
          <p className="text-paper/70 text-sm mt-1">Application details</p>
        </div>

        {/* Content */}
        <div className="p-6 lg:p-8 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-5 bg-ember-soft/25 border-l-2 border-ember p-3 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-ember-deep text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-5 bg-olive/15 border-l-2 border-olive p-3 flex items-start gap-2.5">
              <CheckCircle className="w-4 h-4 text-olive flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-olive text-sm">{success}</p>
            </div>
          )}

          {/* Created Credentials Display */}
          {createdCredentials && (
            <div className="mb-6 bg-steel/10 border border-steel/30 p-4">
              <h4 className="font-editorial italic text-lg text-ink-950 flex items-center gap-2 mb-3 tracking-tight">
                <Key className="w-4 h-4 text-ember" strokeWidth={1.5} />
                Account credentials
              </h4>
              <p className="text-sm text-ink-700 mb-3">
                Copy these credentials to send to the retailer manually if the email didn't go through:
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 bg-paper-bright border border-ink-200 p-2">
                  <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500 w-16">Email</span>
                  <code className="flex-1 text-sm font-mono text-ink-900">{createdCredentials.email}</code>
                  <button
                    onClick={() => handleCopy(createdCredentials.email, 'email')}
                    className="p-1 hover:bg-paper transition-colors duration-150"
                    title="Copy email"
                  >
                    <Copy className={`w-4 h-4 ${copiedField === 'email' ? 'text-olive' : 'text-ink-500'}`} strokeWidth={1.5} />
                  </button>
                </div>
                <div className="flex items-center gap-2 bg-paper-bright border border-ink-200 p-2">
                  <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500 w-16">Password</span>
                  <code className="flex-1 text-sm font-mono text-ink-900">{createdCredentials.password}</code>
                  <button
                    onClick={() => handleCopy(createdCredentials.password, 'password')}
                    className="p-1 hover:bg-paper transition-colors duration-150"
                    title="Copy password"
                  >
                    <Copy className={`w-4 h-4 ${copiedField === 'password' ? 'text-olive' : 'text-ink-500'}`} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
              <button
                onClick={onClose}
                className="mt-4 w-full bg-ember hover:bg-ember-deep text-paper-warm py-2.5 text-sm font-medium transition-colors duration-150 ease-editorial"
              >
                Done
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-6">
            {/* Business Info */}
            <div className="space-y-4">
              <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2">
                <Building2 className="w-4 h-4 text-ember" strokeWidth={1.5} />
                Business information
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Business name</p>
                  <p className="font-medium text-ink-950">{request.businessName}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Business type</p>
                  <p className="font-medium text-ink-950">{request.businessType}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Tax ID / HST number</p>
                  <p className="font-medium font-mono text-ink-950">{request.taxId}</p>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2">
                <User className="w-4 h-4 text-ember" strokeWidth={1.5} />
                Contact information
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-1">Contact name</p>
                  <p className="font-medium text-ink-950">{request.contactName}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                  <p className="text-ink-900">{request.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-ink-500" strokeWidth={1.5} />
                  <p className="text-ink-900">{request.phone}</p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="col-span-2 space-y-4">
              <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2">
                <MapPin className="w-4 h-4 text-ember" strokeWidth={1.5} />
                Business address
              </h3>
              <div className="bg-paper border border-ink-200 p-4 text-sm text-ink-900">
                <p>{request.address.street}</p>
                <p>{request.address.city}, {request.address.province} {request.address.postalCode}</p>
                <p>{request.address.country}</p>
              </div>
            </div>

            {/* Submission Info */}
            <div className="col-span-2 space-y-4">
              <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight flex items-center gap-2">
                <FileText className="w-4 h-4 text-ember" strokeWidth={1.5} />
                Application status
              </h3>
              <div className="bg-paper border border-ink-200 p-4 text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-ink-500">Submitted</span>
                  <span className="font-medium text-ink-900">{request.submittedAt.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-ink-500">Status</span>
                  <span className={`font-medium capitalize ${
                    request.status === 'approved' ? 'text-olive' :
                    request.status === 'rejected' ? 'text-ember' : 'text-brass'
                  }`}>
                    {request.status}
                  </span>
                </div>
                {request.reviewedAt && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Reviewed</span>
                      <span className="font-medium text-ink-900">{request.reviewedAt.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-ink-500">Reviewed by</span>
                      <span className="font-medium text-ink-900">{request.reviewedBy}</span>
                    </div>
                  </>
                )}
                {request.rejectionReason && (
                  <div className="pt-2 border-t border-ink-200">
                    <span className="font-mono text-[10px] tracking-micro uppercase text-ink-500 block mb-1">Rejection reason</span>
                    <p className="text-ember">{request.rejectionReason}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Rejection Form */}
          {showRejectForm && (
            <div className="mt-6 p-4 border border-ember/30 bg-ember-soft/15">
              <label className="block font-mono text-[10px] tracking-micro uppercase text-ember-deep mb-2">
                Rejection reason
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-4 py-3 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150"
                rows={3}
                placeholder="Please explain why this application is being rejected..."
              />
            </div>
          )}
        </div>

        {/* Actions */}
        {isPending && (
          <div className="px-6 lg:px-8 py-5 border-t border-ink-200 bg-paper flex gap-3 justify-end">
            {!showRejectForm ? (
              <>
                <button
                  onClick={() => setShowRejectForm(true)}
                  className="px-4 py-2.5 text-ember hover:bg-ember-soft/25 text-sm font-medium transition-colors duration-150 flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" strokeWidth={1.5} />
                  Reject
                </button>
                <button
                  onClick={handleApprove}
                  disabled={isApproving}
                  className="px-5 py-2.5 bg-olive hover:bg-olive/90 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial flex items-center gap-2 disabled:opacity-50"
                >
                  {isApproving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                      Approve & send credentials
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowRejectForm(false)}
                  className="px-4 py-2.5 text-ink-700 hover:bg-paper-bright border border-ink-200 hover:border-ink-300 text-sm font-medium transition-colors duration-150"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={isRejecting}
                  className="px-5 py-2.5 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial flex items-center gap-2 disabled:opacity-50"
                >
                  {isRejecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                      Rejecting...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" strokeWidth={1.5} />
                      Confirm rejection
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestDetailModal;
